import clsx from 'clsx'
import Empty from 'components/Empty'
import FieldDetail from 'components/FieldDetail'
import { useStore } from 'context/store'
import dayjs from 'dayjs'
import { observer } from 'mobx-react'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { Divider } from 'primereact/divider'
import { ProgressSpinner } from 'primereact/progressspinner'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import { CustomerStatusEnum } from 'utils/constants/customer'
import { DATE_TIME_FORMAT } from 'utils/constants/datetime'
import { formatPhone } from 'utils/helper/common-helpers'
import { getRegistrationStatusTag } from 'utils/helper/table'
import ModalResendMail from './ModalResendMail'
import RegisteredHistory from './RegisterHistory'

const RegistrationDetail = () => {
  const { t } = useTranslation()
  const {
    eventRegistrationStore: {
      fetchDetail,
      selectedItem,
      loadingDetail,
      setSelectedItem,
      loadingSubmit,
      block,
    },
  } = useStore()

  const { registrationId } = useParams()
  useEffect(() => {
    if (registrationId) fetchDetail(registrationId)

    return () => {
      setSelectedItem(null)
    }
  }, [fetchDetail, registrationId, setSelectedItem])

  return (
    <div
      className={clsx(
        'card bg-white border-round-xl px-3 py-3 md:px-4 md:py-5'
      )}
    >
      {loadingDetail ? (
        <div className="w-full h-full flex justify-content-center align-items-center">
          <ProgressSpinner />
        </div>
      ) : selectedItem ? (
        <>
          <div
            className={clsx(
              'flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-4'
            )}
          >
            <div className="flex flex-wrap align-items-center gap-2 md:gap-4 flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl text-primary font-bold m-0 line-height-3">
                {t('Registrations details')}
              </h1>
              {getRegistrationStatusTag(selectedItem.status)}
            </div>
          </div>
          <Divider />

          <h2 className="text-xl font-bold mb-2">{t('Customer')}</h2>
          <FieldDetail label={t('Name')} value={selectedItem.name} />
          <FieldDetail label={t('Furigana')} value={selectedItem.furigana} />
          <FieldDetail label={t('Email')} value={selectedItem.email} />
          <FieldDetail
            label={t('Phone Number')}
            value={formatPhone(selectedItem.phoneNumber)}
          />

          <Divider />
          <h2 className="text-xl font-bold mb-2">{t('Event Information')}</h2>
          <FieldDetail
            label={t('Registering event')}
            value={selectedItem.event.title}
          />
          <FieldDetail
            label={t('Time slot')}
            value={`${dayjs(selectedItem.timeslot?.startTime).format(DATE_TIME_FORMAT.HOUR)}-${dayjs(selectedItem.timeslot?.endTime).format(DATE_TIME_FORMAT.HOUR)}`}
          />
          <FieldDetail
            label={t('Registed Time')}
            value={dayjs(selectedItem.createdAt).format(
              DATE_TIME_FORMAT.FULL_SECOND
            )}
          />
          <FieldDetail label={t('Guest')} value={selectedItem.guest} />
          <FieldDetail
            label={selectedItem.event?.remarksLabel?.trim?.() || t('Note')}
            value={selectedItem.note}
          />

          <Divider />

          <RegisteredHistory />
          <FieldDetail
            label={t('About this Customer')}
            value={selectedItem.customer?.note}
          />
          <Divider />

          <div
            className={clsx(
              'flex flex-column md:flex-row md:justify-content-between align-items-stretch md:align-items-center gap-3 w-full'
            )}
          >
            {selectedItem.customer.status === CustomerStatusEnum.Blocked ? (
              <div className="hidden md:block" />
            ) : (
              <div className="flex flex-column sm:flex-row gap-3 w-full md:w-auto">
                <Link
                  to="edit"
                  className="w-full sm:flex-1 md:flex-none md:w-auto"
                >
                  <Button
                    label={t('Go To Edit')}
                    className="w-full md:w-auto"
                  />
                </Link>

                <div className="w-full sm:flex-1 md:flex-none md:w-auto">
                  <ModalResendMail
                    registrationId={registrationId}
                    className="w-full md:w-auto"
                  />
                </div>
              </div>
            )}

            <div className="flex flex-column sm:flex-row gap-3 w-full md:w-auto md:justify-content-end">
              {selectedItem.customer.status === CustomerStatusEnum.Active ? (
                <Button
                  label={t('Block')}
                  className="uppercase w-full md:w-auto"
                  severity="secondary"
                  loading={loadingSubmit}
                  onClick={() => {
                    confirmDialog({
                      message: t('doyouwant', {
                        action: t('block this user'),
                      }),
                      header: t('confirmation', {
                        action: t('Block'),
                      }),
                      acceptLabel: t('Yes'),
                      rejectLabel: t('No'),
                      accept: async () => {
                        try {
                          await block(selectedItem.customer._id, true)
                          await fetchDetail(registrationId)
                        } catch (error) {}
                      },
                    })
                  }}
                />
              ) : (
                <Button
                  label={t('Unblock')}
                  className="uppercase w-full md:w-auto"
                  outlined
                  loading={loadingSubmit}
                  onClick={() => {
                    confirmDialog({
                      message: t('doyouwant', {
                        action: t('unblock this user'),
                      }),
                      header: t('confirmation', {
                        action: t('Unblock'),
                      }),
                      acceptLabel: t('Yes'),
                      rejectLabel: t('No'),
                      accept: async () => {
                        try {
                          await block(selectedItem.customer._id, false)
                          await fetchDetail(registrationId)
                        } catch (error) {}
                      },
                    })
                  }}
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <Empty />
      )}
    </div>
  )
}

export default observer(RegistrationDetail)
